import React, { useEffect, useRef, useState } from 'react';
import useScopeSwitch from '../../hooks/useScopeSwitch';

/**
 * Which customer, org and tenant this wizard is working in — and how to change it.
 *
 * <h3>Why the wizard needs this at all</h3>
 *
 * Someone who belongs to more than one customer does not always arrive in the
 * one they mean to work in, and everything the wizard creates lands in whatever
 * scope the session carries. Without this control the only way out was to leave
 * the wizard, change scope on the platform, and come back — and with nothing on
 * screen naming the scope, there was no way to tell that was even needed.
 *
 * Behaviour comes from {@link useScopeSwitch}, which the platform shell uses
 * too. This file is only how it looks here.
 */
export default function WizardScopeChip() {
  const { active, others, scope, switching, error, switchTo } = useScopeSwitch();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Close on an outside click or Escape, so the menu never strands the page.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Nothing resolved yet — show nothing rather than a chip full of blanks.
  if (!scope.tenantName && !scope.orgName && !scope.customerName && !active) return null;

  const parts = [scope.customerName, scope.orgName, scope.tenantName].filter(Boolean);
  const label = parts.length ? parts.join(' / ') : (active?.tenantName || active?.orgName || '');
  const canSwitch = others.length > 0;

  return (
    <div ref={boxRef} style={{ position: 'relative', minWidth: 0 }}>
      <button
        type="button"
        onClick={() => canSwitch && setOpen((v) => !v)}
        disabled={switching}
        aria-haspopup={canSwitch ? 'menu' : undefined}
        aria-expanded={canSwitch ? open : undefined}
        title={canSwitch ? 'Change which customer and tenant you are working in' : label}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '7px',
          maxWidth: '420px',
          minWidth: 0,
          padding: '5px 9px',
          background: 'var(--p7-surface-sunken)',
          border: '1px solid var(--p7-line)',
          borderRadius: '6px',
          font: 'var(--p7-text-caption)',
          fontFamily: 'var(--p7-font-sans)',
          color: 'var(--p7-ink-muted)',
          cursor: canSwitch && !switching ? 'pointer' : 'default',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        {scope.role ? <span className="p7-status">{scope.role}</span> : null}
        {canSwitch ? (
          <span aria-hidden="true" style={{ color: 'var(--p7-ink-faint)' }}>
            {switching ? '…' : '▾'}
          </span>
        ) : null}
      </button>

      {open && canSwitch ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 40,
            minWidth: '260px',
            maxHeight: '300px',
            overflowY: 'auto',
            padding: 'var(--p7-space-2)',
            background: 'var(--p7-surface)',
            border: '1px solid var(--p7-line)',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,.10)',
          }}
        >
          <div className="p7-eyebrow" style={{ padding: '4px 8px 6px' }}>
            Work in
          </div>
          {others.map((m) => (
            <button
              key={m.membershipId}
              type="button"
              role="menuitem"
              disabled={switching}
              onClick={() => switchTo(m.membershipId)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '7px 8px',
                border: 0,
                borderRadius: '6px',
                background: 'transparent',
                cursor: switching ? 'default' : 'pointer',
                minWidth: 0,
              }}
            >
              <span
                style={{
                  display: 'block',
                  font: 'var(--p7-text-ui)',
                  fontFamily: 'var(--p7-font-sans)',
                  fontWeight: 600,
                  color: 'var(--p7-ink)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {m.tenantName || m.orgName || 'Unnamed scope'}
              </span>
              <span
                style={{
                  display: 'block',
                  font: 'var(--p7-text-caption)',
                  fontFamily: 'var(--p7-font-sans)',
                  color: 'var(--p7-ink-subtle)',
                }}
              >
                {m.role}{m.orgName ? ` · ${m.orgName}` : ''}
              </span>
            </button>
          ))}
          {error ? (
            <div className="p7-note p7-note--deny" style={{ marginTop: 'var(--p7-space-2)' }}>
              {error}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
